import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { canContribute, canCreateRecordType } from '../../hartUtils';
import { ASSOCIATION_TO_TABLE_TYPE } from './config';
import AssociationTable from './AssociationTable';
import EditableAssociation from './EditableAssociation';
import { useAvailableRecords } from './hooks/useAvailableRecords';

const AssociationAccordion = ({
    config,
    data = [],
    loading = false,
    error = null,
    parentId,
    parentType,
    projectId,
    editable = false,
    assignment = [],
    onChange,
}) => {
    const navigate = useNavigate();

    // Fetch available records for the dropdowns in edit mode
    const { allRecords: fetchedRecords, loading: loadingRecords } = useAvailableRecords(config, editable);

    // If no endpoint is provided, the "available records" are just the ones we already have assigned
    // This allows editing the metadata of existing links without adding new ones if the list isn't fetched
    const allRecords = useMemo(() => {
        if (config.availableRecordsEndpoint) {
            return fetchedRecords;
        }
        // Fallback: use existing data as the "universe" of records
        const idField = config.idField || 'id';
        return data.map(item => (
            (item && typeof item === 'object') ? item : { [idField]: item }
        ));
    }, [config.availableRecordsEndpoint, fetchedRecords, data, config.idField]);

    const count = data.length;

    const addNewTableType = ASSOCIATION_TO_TABLE_TYPE[config.key];
    const addNewPath = addNewTableType && projectId
        ? `/projects/${projectId}/hart/new/${addNewTableType}?sourceTable=${parentType}&sourceId=${parentId}&associationType=${config.key}`
        : null;

    // Configuration for the EditableAssociation component
    const editConfig = useMemo(() => ({
        fields: config.editableFields || [],
        getOptionLabel: config.getOptionLabel || ((option) => option?.name || option?.[config.idField || 'id'] || ''),
        idField: config.idField || 'id',
        dataField: config.dataField || 'id',
        onlyAllowUnique: config.onlyAllowUnique !== false, // default true
        emptyStateText: config.emptyStateText || `No ${config.name.toLowerCase()} assigned.`,
        addButtonText: config.addButtonText || `Assign ${config.name}`,
    }), [config]);

    return (
        <Accordion
            defaultExpanded={config.defaultExpanded === true}
            sx={{
                bgcolor: 'background.paper',
                '&:before': { display: 'none' },
                mb: 2,
                '&.Mui-expanded': {
                    mb: 2,
                },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    bgcolor: 'background.paper',
                    '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '& .MuiTypography-root': {
                            color: 'white',
                        },
                        '& .MuiIconButton-root:not([aria-label*="Add new"])': {
                            color: 'white',
                        },
                    },
                    minHeight: 58,
                    '&.Mui-expanded': {
                        minHeight: 58,
                    },
                    '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        my: 0,
                        '&.Mui-expanded': {
                            my: 0,
                        },
                    },
                }}
            >
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {config.name}
                </Typography>
                {!loading && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mr: 2 }}>
                        ({count})
                    </Typography>
                )}
                {editable && canContribute(projectId) && !config.disableAdd && canCreateRecordType(addNewTableType, projectId) && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (addNewPath) {
                                navigate(addNewPath);
                            }
                        }}
                        sx={{
                            mr: 1,
                            bgcolor: 'secondary.main',
                            color: 'white',
                            borderRadius: '50%',
                            '&:hover': {
                                bgcolor: 'white',
                                color: 'secondary.main',
                            },
                        }}
                        aria-label={`Add new ${config.name.toLowerCase()}`}
                        title={`Add new ${config.name.toLowerCase()}`}
                    >
                        <AddIcon />
                    </IconButton>
                )}
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ overflow: 'auto' }}>
                    {config.isEditable && editable ? (
                        <EditableAssociation
                            assignments={assignment}
                            allRecords={allRecords}
                            loadingRecords={loadingRecords}
                            config={editConfig}
                            onChange={onChange}
                        />
                    ) : (
                        <AssociationTable
                            data={data}
                            config={config}
                            loading={loading}
                            error={error}
                            projectId={projectId}
                        />
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

AssociationAccordion.propTypes = {
    config: PropTypes.object.isRequired,
    data: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.string,
    parentId: PropTypes.string.isRequired,
    parentType: PropTypes.string.isRequired,
    projectId: PropTypes.string,
    editable: PropTypes.bool,
    assignment: PropTypes.array,
    onChange: PropTypes.func,
};

export default AssociationAccordion;
